/**
 * Main entry point for the app.
 * Nothing should be exported from this file, because it runs too much
 * code when it is imported. This should only be run once, when the
 * main app loads for a user visiting the site.
 */
// react-hot-loader needs to be loaded before react & react-dom!
import * as _Hot from 'react-hot-loader';  // eslint-disable-line

/* tslint:disable:ordered-imports */
import 'assets/scss/base.scss';
import 'assets/scss/app.scss';
/* tslint:enable:ordered-imports */

import 'core-js/stable';
import 'regenerator-runtime/runtime';

import { bind } from 'bind-decorator';
import { ConnectedRouter, routerMiddleware } from 'connected-react-router';
import { History } from 'history';
import { announce, focusElement } from 'oaf-side-effects';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { Helmet } from 'react-helmet';
import { IntlProvider } from 'react-intl';
import { Provider } from 'react-redux';
import { Route, RouteComponentProps } from 'react-router';
import { applyMiddleware, createStore, Store } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension';
import thunk from 'redux-thunk';

import { checkLoginStatus } from 'auth/actions';
import { fetchBannersNow } from 'banners/actions';
import { LxConfig, ROUTES } from 'global/constants';
import {
    DEFAULT_UI_LANGUAGE,
    locale,
    localeMessages,
} from './i18n';
import { fetchNotificationsPoll } from 'notifications/polls';
import { MainRoutes } from 'routes/main';
import messages from 'ui/components/displayMessages';
import { GlobalMessageReporter } from 'ui/components/GlobalMessageReporter/GlobalMessageReporter';
import { history } from './global/history';
import { createRootReducer, RootState } from './global/reducers';
import { setStore } from './global/store';
import { handleTranslationError, intl } from 'i18n';
import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';

///////////////////////////////////////////////
// Sentry
if (LxConfig.SentryDsn) {
    Sentry.init({
        dsn: LxConfig.SentryDsn,
        integrations: [new BrowserTracing()],

        // Set tracesSampleRate to 1.0 to capture 100%
        // of transactions for performance monitoring.
        // We recommend adjusting this value in production
        tracesSampleRate: 1.0,
        release: LxConfig.CommitSha,
    });
}

///////////////////////////////////////////////
// Favicons
import Favicon192 from 'assets/images/favicon-192x192.png';  // eslint-disable-line
import Favicon32 from 'assets/images/favicon-32x32.png';  // eslint-disable-line

// Pollyfills for IE11 etc. fetch() API
import 'whatwg-fetch';  // eslint-disable-line

///////////////////////////////////////////////
// Helper methods

/** Are two Sets equal? */
function areSetsEqual<T>(a: ReadonlySet<T>, b: ReadonlySet<T>) {
    return a.size === b.size && [...a].every((value) => b.has(value));
}



///////////////////////////////////////////////
// Redux store
function configureStore(useHistory: History, initialState?: RootState): Store<RootState> {
    let middleware = applyMiddleware(routerMiddleware(useHistory), thunk);

    if (process.env.NODE_ENV !== 'production') {
        middleware = composeWithDevTools(middleware);
    }

    const newStore = createStore(
        createRootReducer(history) as any, initialState as any, middleware,
    ) as Store<RootState>;

    if (module.hot) {
        module.hot.accept('global/reducers', () => {
            const nextCreateRootReducer = require('global/reducers');
            store.replaceReducer(nextCreateRootReducer(history));
        });
    }

    return newStore;
}
const store = configureStore(history);
setStore(store);

///////////////////////////////////////////////
// Component that scrolls the window to the top when changing routes.
class ScrollToTop extends React.Component<RouteComponentProps<{}>> {
    public componentDidUpdate(prevProps: RouteComponentProps<{}>) {
        if (this.props.location !== prevProps.location) {
            const path = this.props.location.pathname.replace(/\/$/, ''); // Path without trailing slash
            const prevPath = prevProps.location.pathname.replace(/\/$/, ''); // Path without trailing slash
            if (path === prevPath && [ROUTES.Library.HOME, ROUTES.People.HOME].includes(path)) {
                // We are on a search page. If the only change is to the filters, we don't want to scroll back to top.
                // Otherwise there can be an annoying scroll while the user is changing the filters.
                // But we do want to scroll back to top when the search keyword or page has changed.
                const params = new URLSearchParams(this.props.location.search);
                const oldParams = new URLSearchParams(prevProps.location.search);
                if (!areSetsEqual(new Set(params.getAll('t')), new Set(oldParams.getAll('t')))) {
                    return; // Filters have changed. Don't scroll.
                }
            }
            // Set focus to the document.
            focusElement(document.documentElement);
            // Scroll back to the top of the page, or the top of the .lx-scroll-top-on-navigate element
            const el = document.querySelector('.lx-scroll-top-on-navigate') as HTMLElement | null;
            window.scrollTo(0, el ? el.offsetTop : 0);
        }
    }

    public render() {
        return null;
    }
}

// Check if the user is logged in
checkLoginStatus(store.dispatch);

// Location change hooks
history.listen((location, action) => {
    if (location.pathname === '/') {
        checkLoginStatus(store.dispatch);
    }
    // Update Google Analytics to the new page
    // https://developers.google.com/analytics/devguides/collection/gtagjs/single-page-applications
    if (window.enableGoogleAnalytics) {  // enableGoogleAnalytics is set in index.html
        window.gtag('config', window.GA_MEASUREMENT_ID, { page_path: location.pathname });
    }
});

// Fetch banners
fetchBannersNow();

// Start polls
fetchNotificationsPoll();

const srNavigationAnnouncementsDivId = 'navigation-announcements';
const srNavigationAnnouncementsDiv = (
    <div id={srNavigationAnnouncementsDivId} className='sr-only' aria-live='polite' aria-atomic='true' />
);

export class HelmetComponent extends React.PureComponent<{}> {
    private lastTitle: string | null = null;

    public render() {
        return (
            <Helmet onChangeClientState={this.onChangeClientState}>
                <html lang={locale} />
                <link rel='icon' type='image/png' sizes='32x32' href={Favicon32} />
                <link rel='icon' type='image/png' sizes='192x192' href={Favicon192} />
                <title>{this.defaultPageTitle()}</title>
            </Helmet>
        );
    }

    private defaultPageTitle() {
        return intl.formatMessage(messages.defaultPageTitle);
    }

    @bind private onChangeClientState(newState: { title: string }) {
        const newTitle = newState.title;
        if (newTitle !== this.lastTitle && newTitle !== this.defaultPageTitle()) {
            const announcement = intl.formatMessage(
                messages.srPageNavigationAnnouncement, { pageTitle: newTitle },
            );
            announce(announcement, srNavigationAnnouncementsDivId);
        }
        this.lastTitle = newTitle;
    }
}

ReactDOM.render(
    <IntlProvider locale={locale} defaultLocale={DEFAULT_UI_LANGUAGE.key} textComponent={React.Fragment} messages={localeMessages} onError={handleTranslationError}>
        <Provider store={store}>
            <ConnectedRouter history={history}>
                <>
                    <HelmetComponent />
                    <Route component={ScrollToTop} />
                    <MainRoutes />
                    <GlobalMessageReporter />
                    {srNavigationAnnouncementsDiv}
                </>
            </ConnectedRouter>
        </Provider>
    </IntlProvider>,
    document.getElementById('root'),
);

if (LxConfig.UserSnapUrl) {
    // tslint:disable-next-line: only-arrow-functions
    (function () {
        const s = document.createElement('script');
        s.type = 'text/javascript';
        s.async = true;
        s.src = LxConfig.UserSnapUrl;
        const x = document.getElementsByTagName('script')[0];
        if (x.parentNode) {
            x.parentNode.insertBefore(s, x);
        }
    })();
}
